import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import Http from "../../Http";
import { Insurance_API } from "../../Helper";
import AdBanner from "../AdPage/AdBanner";
export default function HealthInsuranceCalculator() {
      const [insuranceData, setInsuranceData] = useState([]);
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    age: 30,
    sumAssured: 500000,
    tenure: 1,
    members: 1,
    preexisting: "no",
  });

  const [result, setResult] = useState(null);
  const [pieData, setPieData] = useState([]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: ["age", "sumAssured", "members", "tenure"].includes(id)
        ? parseInt(value)
        : value,
    });
  };

  const handleSliderChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: parseInt(value),
    });
  };

  const formatIndianCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(value);
  };

  const calculatePremium = () => {
    const { age, sumAssured, tenure, members, preexisting } = formData;

    // Base rate calculation
    let baseRate = 0.008; // 0.8% annually of sum assured

    // Additional rate for age above 40
    if (age > 40) baseRate += 0.003;

    // Additional rate for pre-existing conditions
    if (preexisting === "yes") baseRate += 0.004;

    // Calculate premium
    let premium = sumAssured * baseRate * tenure;

    // Multiply by number of members
    premium *= members;

    // Calculate GST
    const gst = premium * 0.18;

    // Calculate total
    const total = premium + gst;

    // Set result
    const newResult = {
      premium,
      gst,
      total,
      tenure,
    };

    setResult(newResult);

    // Update pie chart data
    setPieData([
      { name: "Base Premium", value: premium },
      { name: "GST", value: gst },
    ]);
  };

  // Calculate on initial render
  useEffect(() => {
    calculatePremium();
  }, []);

  // Calculate whenever form data changes
  useEffect(() => {
    calculatePremium();
  }, [formData]);

  const COLORS = ["#0088FE", "#00C49F"];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        question: 'What is the purpose of a health insurance premium calculator?',
        answer: 'It helps you estimate your health insurance premium based on your personal and medical details.',
      },
      {
        question: 'Is the premium calculator free to use?',
        answer: 'Yes, TheFinmaster’s premium calculator is 100% free with no hidden charges.',
      },
      {
        question: 'Do I need to share documents to use the calculator?',
        answer: 'No, only basic inputs like age, city, and sum insured are required.',
      },
      {
        question: 'How accurate is the premium estimate?',
        answer: 'It’s highly accurate and closely reflects real-time insurer quotations.',
      },
      {
        question: 'Can I add riders while calculating the premium?',
        answer: 'Yes, you can select optional add-ons like maternity cover or critical illness.',
      },
      {
        question: 'What happens if I input incorrect details?',
        answer: 'The premium estimate may be inaccurate. Always provide correct details.',
      },
      {
        question: 'Will using the calculator affect my credit or insurance eligibility?',
        answer: 'Not at all. It’s a non-intrusive tool meant for guidance.',
      },
      {
        question: 'Does it show tax benefits too?',
        answer: 'Some calculators also display potential tax deductions under Section 80D.',
      },
      {
        question: 'Can I compare policies from different insurers?',
        answer: 'Yes, TheFinmaster’s tool allows side-by-side comparison.',
      },
      {
        question: 'Is the estimate same for individuals and family plans?',
        answer: 'No, premiums differ for individual and family floater policies.',
      },
      {
        question: 'Why does my city affect the premium?',
        answer: 'Because healthcare costs vary from one city to another.',
      },
      {
        question: 'How do age and smoking affect the premium?',
        answer: 'Older age and smoking habits increase the risk and hence the premium.',
      },
      {
        question: 'Can I save my quote for future reference?',
        answer: 'Yes, many calculators provide an option to download or email the quote.',
      },
      {
        question: 'How often should I use the calculator?',
        answer: 'Use it whenever your personal details or health condition changes.',
      },
      {
        question: 'Does it also show policy benefits?',
        answer: 'Yes, you can view inclusions, exclusions, and other policy features.',
      },
    ];
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  useEffect(() => {
    fetchInsuranceData();
  }, []);
  const fetchInsuranceData = async () => {
    try {
      const response = await Http.get(Insurance_API);

      // The response structure might have nested data
      // Check if response has an insurance property (like in the original fetch response)
      const data = response.insurance || response;

      // Filter the insurance data to show only Car category
      const carInsuranceData = Array.isArray(data)
        ? data.filter((insurance) => insurance.category_name === "Health")
        : [];

      setInsuranceData(carInsuranceData);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch insurance data");
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };
  return (
    <>
     <Helmet>
         <title>Health Insurance Calculator – Premium & Coverage Estimator</title>
         <meta  name='description' content='https://thefinmaster.com/financial-tools-calculators/health-insurance-calculator'/>
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/health-insurance-calculator" /> 
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Health Calculator</span>
        </li> 
      
      </ol>
    </nav> */}
       <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className="bg-gray-100 min-h-screen p-4 md:p-">
        <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 md:p-8">
            <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8">
              Health Insurance Premium Calculator
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Form Section */}
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="age"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Age
                  </label>
                  <input
                    type="number"
                    id="age"
                    min="18"
                    max="60"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="Enter your age"
                    required
                  />
                  <input
                    type="range"
                    id="age"
                    min="18"
                    max="60"
                    value={formData.age}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>18 Yr</span>
                    <span>60 Yr</span>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="sumAssured"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Sum Assured (₹)
                  </label>
                  <input
                    type="number"
                    id="sumAssured"
                    min="50000"
                    max="10000000"
                    step="50000"
                    value={formData.sumAssured}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="Enter sum assured"
                    required
                  />
                  <input
                    type="range"
                    id="sumAssured"
                    min="50000"
                    max="10000000"
                    step="50000"
                    value={formData.sumAssured}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹50,000</span>
                    <span>₹1 Crore</span>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="tenure"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Policy Tenure (years)
                  </label>
                  <select
                    id="tenure"
                    value={formData.tenure}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option value="1">1 Year</option>
                    <option value="2">2 Years</option>
                    <option value="3">3 Years</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="members"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Number of Members Covered
                  </label>
                  <input
                    type="number"
                    id="members"
                    min="1"
                    max="6"
                    value={formData.members}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="Enter family members"
                    required
                  />
                  <input
                    type="range"
                    id="members"
                    min="1"
                    max="6"
                    value={formData.members}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 Member</span>
                    <span>6 Members</span>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="preexisting"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Pre-existing Diseases
                  </label>
                  <select
                    id="preexisting"
                    value={formData.preexisting}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>

                <button
                  onClick={calculatePremium}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition duration-300"
                >
                  Calculate Estimated  Premium
                </button>
              </div>

              {/* Results Section */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-6">
                  Results
                </h2>

                {result && (
                  <div className="space-y-6">
                    <div className="bg-white p-5 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-gray-700 mb-4">
                      Estimated   Premium Details
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Age:</span>
                          <span className="font-medium">
                            {formData.age} years
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">Sum Assured:</span>
                          <span className="font-medium">
                            {formatIndianCurrency(formData.sumAssured)}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">Policy Tenure:</span>
                          <span className="font-medium">
                            {formData.tenure} year
                            {formData.tenure > 1 ? "s" : ""}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Members Covered:
                          </span>
                          <span className="font-medium">
                            {formData.members}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Pre-existing Diseases:
                          </span>
                          <span className="font-medium">
                            {formData.preexisting === "yes" ? "Yes" : "No"}
                          </span>
                        </div>

                        <div className="border-t border-gray-200 my-3"></div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Base Premium (for {result.tenure} year
                            {result.tenure > 1 ? "s" : ""}):
                          </span>
                          <span className="font-medium">
                            {formatIndianCurrency(result.premium)}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">GST (18%):</span>
                          <span className="font-medium">
                            {formatIndianCurrency(result.gst)}
                          </span>
                        </div>

                        <div className="border-t border-gray-200 my-3"></div>

                        <div className="flex justify-between text-lg font-bold">
                          <span className="text-gray-800">Total Estimated  Payable:</span>
                          <span className="text-blue-600">
                            {formatIndianCurrency(result.total)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-5 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">
                      Estimated   Premium Breakdown
                      </h3>
                      <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {pieData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) => formatIndianCurrency(value)}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is a Health Insurance Premium Calculator?
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            A Health Insurance Premium Calculator is an intuitive online tool that helps individuals estimate the premium they will need to pay for a health insurance policy. This tool considers various factors such as age, sum insured, medical history, policy tenure, and the number of insured members to generate an approximate premium amount. It eliminates the need for manual calculations and helps in transparent decision-making. By using this calculator, users can compare various health insurance plans based on affordability and coverage benefits.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Health insurance calculators are widely used by first-time buyers, families, and senior citizens who want to understand what their policy may cost before committing to a plan. It simplifies the otherwise complex process of health insurance selection and empowers users with clarity.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Health Insurance Premium?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Health insurance premium refers to the fixed amount that a policyholder pays, either monthly, quarterly, or annually, to keep their health insurance policy active. This payment ensures continuous coverage for medical expenses such as hospitalization, surgeries, pre-and post-hospitalization costs, daycare procedures, and more.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The premium is determined based on several risk factors like age, health conditions, sum insured, policy features, and lifestyle habits such as smoking. In return for paying the premium regularly, the insurance company promises to cover the insured's medical expenses as per the policy terms.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Understanding your premium is critical because it directly impacts your budget, the scope of coverage, and your ability to make future claims. A lower premium may indicate basic coverage, while a higher premium might offer more comprehensive protection.
            </p>
          </div>
          <div className="container mx-auto px-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
          Popular Health Insurance Cards
            </h2>
  <div className="flex flex-col items-center space-y-6 mt-4">
    
  {insuranceData.slice(0, 5).map((insurance) => (
      <div
        key={insurance.id}
        className="w-full max-w-4xl bg-white rounded-2xl shadow hover:shadow-lg transition-shadow duration-300 p-4"
      >
        <div className="flex flex-col md:flex-row items-center md:items-start md:justify-between gap-6">
         
          <Link to={`/health-insurance/${insurance.page_url}`}
            className="w-full md:w-[180px] flex justify-center md:justify-start"
          >
            <img
              src={insurance.image}
              alt={insurance.page_title || "Insurance Provider"}
              className="h-18 object-contain"
            />
          </Link>

   
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center md:text-left">
            <div>
              <p className="text-gray-500 text-sm font-medium">Premium Starting</p>
              <h4 className="text-lg font-bold">
              ₹{insurance.premium_start}
                
              </h4>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">Claim Settlement</p>
              <h4 className="text-lg font-bold">{insurance.claim_settlement}%</h4>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">Hospital Networks</p>
              <h4 className="text-lg font-bold">{insurance.network_garages}+</h4>
            </div>
          </div>


          <div className="flex flex-col sm:flex-row items-center gap-2 mt-4 md:mt-0">
            <a
              href={insurance.bank_link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded w-full sm:w-auto">
                Get Quote!
              </button>
            </a>
              <Link to={`/health-insurance/${insurance.page_url}`} className="w-full sm:w-auto">
              <button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded w-full sm:w-auto">
                See Details
              </button>
            </Link>
          </div>
        </div>
      </div>
    ))}
  </div>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use a Health Insurance Premium Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using a health insurance premium calculator is simple and straightforward. Here’s a step-by-step guide to help you understand the process:
            </p>
            <ul className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Visit the Calculator Page:</strong> Navigate to a trusted platform like TheFinmaster’s health insurance calculator.</li>
              <li><strong>Enter Basic Details:</strong> Input your age, gender, and city of residence.</li>
              <li><strong>Add Family Members (if needed):</strong> Mention details about other members to be included in the plan, such as spouse, children, or parents.</li>
              <li><strong>Choose Sum Insured:</strong> Select the total coverage amount you wish to opt for.</li>
              <li><strong>Policy Tenure:</strong> Choose the duration for which you want the policy.</li>
              <li><strong>Additional Riders (Optional):</strong> Choose add-ons like critical illness, room rent waiver, maternity cover, etc.</li>
              <li><strong>Click ‘Calculate’:</strong> The calculator will instantly display an estimated premium based on your inputs.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This tool can also be used to compare premiums across insurers and plans to help you select the best-suited option.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is the Health Insurance Premium Formula?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              While every insurer may have a slightly different algorithm, the base formula to calculate health insurance premium is:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Premium = Base Rate + (Sum Insured × Risk Factor × Age Multiplier) + Add-ons – Discounts (if applicable)
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Base Rate</strong> is the starting price for a specific policy.</li>
              <li><strong>Risk Factor</strong> includes pre-existing conditions, lifestyle habits like smoking, BMI, etc.</li>
              <li><strong>Age Multiplier</strong> increases the premium with age.</li>
              <li><strong>Add-ons</strong> include optional riders.</li>
              <li><strong>Discounts</strong> can apply for long-term policies, no-claim bonuses, or voluntary co-payments.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Note: GST and other charges are usually added to the final premium.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Real-Life Example: How the Calculator Works
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Case Study:</strong> Rajiv, aged 40, is looking for a health insurance plan for himself, his 38-year-old wife, and their 12-year-old daughter. He wants ₹10 lakh coverage with maternity and critical illness add-ons.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Steps Taken:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Entered ages and city (Pune)</li>
              <li>Chose sum insured: ₹10 lakh</li>
              <li>Opted for 1-year tenure</li>
              <li>Selected two add-ons: maternity and critical illness</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Result:</strong> The calculator showed a premium estimate of ₹21,000 annually. Rajiv also viewed alternative plans ranging from ₹19,500 to ₹24,000 and could compare benefits side-by-side.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Information Required for the Premium Calculation
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To accurately calculate your health insurance premium, the calculator typically asks for the following information:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Age and Gender of All Insured Members</li>
              <li>City or Zone of Residence</li>
              <li>Pre-existing Diseases (if any)</li>
              <li>Type of Plan (Individual, Family Floater, Senior Citizen, etc.)</li>
              <li>Sum Insured Required</li>
              <li>Policy Tenure (1, 2, or 3 years)</li>
              <li>Lifestyle Habits (e.g., Smoking or Drinking)</li>
              <li>Add-on Covers (Maternity, Critical Illness, etc.)</li>
              <li>Preferred Room Rent Limit or Hospital Room Category</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Providing accurate details ensures the most precise premium estimation and eliminates discrepancies at the time of claim.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Significant Benefits of Health Insurance Premium Calculator
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant Results:</strong> Get real-time premium estimates within seconds.</li>
              <li><strong>Transparency:</strong> Understand how various inputs impact your premium.</li>
              <li><strong>Budget Planning:</strong> Align your policy with your financial goals.</li>
              <li><strong>Comparison Feature:</strong> Easily compare multiple plans to find the best value.</li>
              <li><strong>Customization:</strong> Tailor your policy with riders and adjust the sum insured to meet your needs.</li>
              <li><strong>No Human Error:</strong> Automated calculations reduce the chances of mistakes.</li>
              <li><strong>Free to Use:</strong> Most calculators, including TheFinmaster’s, are 100% free and don’t require any login.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How is Health Insurance Premium Determined?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Premiums are calculated based on a combination of individual risk factors and policy features. Here are the key determinants:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Age:</strong> Older individuals pay higher premiums due to increased health risks.</li>
              <li><strong>Health History:</strong> A history of chronic illness or pre-existing conditions increases the premium.</li>
              <li><strong>Family Medical History:</strong> Hereditary diseases like diabetes or hypertension affect pricing.</li>
              <li><strong>Lifestyle:</strong> Smokers or those with high-risk occupations face higher premiums.</li>
              <li><strong>Sum Insured:</strong> Higher coverage means a higher premium.</li>
              <li><strong>Policy Type:</strong> Individual policies are priced differently from family floaters.</li>
              <li><strong>Geographic Location:</strong> Cities with higher medical costs tend to have higher premiums.</li>
              <li><strong>Policy Term and Riders:</strong> Longer terms or additional features increase the premium amount.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Reduce Health Insurance Premium?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Though premiums are fixed based on several parameters, you can still reduce them through smart planning:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Buy Early:</strong> Younger policyholders enjoy lower premiums.</li>
              <li><strong>Opt for Family Floater Plans:</strong> Covering the whole family under one plan is cheaper.</li>
              <li><strong>Maintain a Healthy Lifestyle:</strong> Regular exercise, no smoking, and healthy eating reduce your risk profile.</li>
              <li><strong>Compare Before Buying:</strong> Use calculators to explore different options.</li>
              <li><strong>Choose Long-Term Policies:</strong> Policies of 2 or 3 years may offer discounts.</li>
              <li><strong>Go for Voluntary Deductibles:</strong> Sharing the financial burden lowers premiums.</li>
              <li><strong>Skip Unnecessary Riders:</strong> Only choose add-ons that are relevant to your health profile.</li>
            </ul>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tax Benefit of Health Insurance
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Health insurance doesn’t just secure your health but also helps save taxes. Here’s how:
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Section 80D of the Income Tax Act:</strong> You can claim deductions on premiums paid:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Up to ₹25,000 for self, spouse, and children</li>
              <li>An additional ₹25,000 for parents (below 60)</li>
              <li>₹50,000 if parents are senior citizens</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Preventive Health Check-ups:</strong> Tax benefits up to ₹5,000 are also allowed under 80D.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Total Deduction:</strong> You can save up to ₹1 lakh per annum if you and your senior citizen parents are covered.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinmaster Health Insurance Premium Calculator
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Personalized Experience:</strong> TheFinmaster calculator adjusts to your unique inputs for tailored results.</li>
              <li><strong>Free & Easy to Use:</strong> No signup, no fee, and no unnecessary jargon.</li>
              <li><strong>Compare Leading Insurers:</strong> See side-by-side comparisons of the top health insurance plans.</li>
              <li><strong>Customizable Coverage:</strong> Add or remove riders and instantly see how your premium changes.</li>
              <li><strong>Trustworthy Insights:</strong> Based on accurate algorithms and updated tax norms.</li>
              <li><strong>User-Centric Interface:</strong> Built to guide even first-time buyers with smooth navigation and helpful tooltips.</li>
            </ul>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
               <Link key={category.id} to={`/category/${category.id}`}>
                 <li className="py-2 border-b border-gray-300">{category.name}</li>
               </Link>
             ))} */}
              </ul>
            </div>
          </div>

             {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
                  <Link key={category.id} to={`/category/${category.id}`}>
                    <li className="py-2 border-b border-gray-300">{category.name}</li>
                  </Link>
                ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
